import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useHistory } from "react-router-dom";
import {
  Card,
  CardContent,
  TextField,
  Box,
  Button,
  Rating,
  FormControlLabel,
  Checkbox,
  Grid,
  Select,
  MenuItem,
} from "@mui/material";
import { createFetch } from "../../utils";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import ThankYouCard from "./ThankYouCard"; // Import the separated ThankYouCard component

const en = {
  feedbackForm: {
    bannerTitle: "Your feedback helps us create exceptional experiences.",
    subtitle: "About your experience",
    nameLabel: "Name",
    namePlaceholder: "Full Name",
    emailLabel: "Email Address",
    emailPlaceholder: "example@mail.com",
    feedbackLabel: "Feedback Comments",
    feedbackPlaceholder: "Share your experience...",
    feedbackRequired: "Feedback comments is required",
    propertyToggleLabel: "Resort or Yacht",
    propertyPelagian: "Pelagian",
    propertyResort: "Resort",
    roomNumberLabel: "Room Number",
    quotationLabel: "Quotation for the Wakatobi Website:",
    quotationHelpText: "Maximum 500 characters.",
    preferencesTitle: "Preferences",
    marketingOptOut: "Please do not use my comments or quotation for your marketing material.",
    newsletterOptIn: "I would like to receive Wakatobi’s newsletter emails.",
    submitButton: "Submit Feedback",
    submittingButton: "Submitting...",
  },
  thankYouCard: {
    title: "Thank You!",
    message: "Your feedback has been received.",
    sharePrompt: "Share your feedback on social media:",
  },
  alerts: {
    feedbackSubmitted: "Feedback already submitted",
    networkError: "Network error: Unable to reach the server.",
    unexpectedError: "Unexpected error occurred.",
    submitError: "Error submitting feedback: ",
  },
};

const de = {
  feedbackForm: {
    bannerTitle: "Ihr Feedback hilft uns, außergewöhnliche Erlebnisse zu schaffen.",
    subtitle: "Über Ihre Erfahrung",
    nameLabel: "Name",
    namePlaceholder: "Vollständiger Name",
    emailLabel: "E-Mail-Adresse",
    emailPlaceholder: "beispiel@mail.com",
    feedbackLabel: "Feedback-Kommentare",
    feedbackPlaceholder: "Teilen Sie Ihre Erfahrungen...",
    feedbackRequired: "Feedback-Kommentare sind erforderlich",
    propertyToggleLabel: "Resort oder Yacht",
    propertyPelagian: "Pelagian",
    propertyResort: "Resort",
    roomNumberLabel: "Zimmernummer",
    quotationLabel: "Zitat für die Wakatobi-Website:",
    quotationHelpText: "Maximal 500 Zeichen.",
    preferencesTitle: "Präferenzen",
    marketingOptOut: "Bitte verwenden Sie meine Kommentare oder Zitate nicht für Ihr Marketingmaterial.",
    newsletterOptIn: "Ich möchte die Newsletter-E-Mails von Wakatobi erhalten.",
    submitButton: "Feedback abschicken",
    submittingButton: "Wird gesendet...",
  },
  thankYouCard: {
    title: "Danke schön!",
    message: "Ihr Feedback wurde erhalten.",
    sharePrompt: "Teilen Sie Ihr Feedback in den sozialen Medien:",
  },
  alerts: {
    feedbackSubmitted: "Feedback bereits übermittelt",
    networkError: "Netzwerkfehler: Der Server kann nicht erreicht werden.",
    unexpectedError: "Unerwarteter Fehler aufgetreten.",
    submitError: "Fehler beim Senden des Feedbacks: ",
  },
};

const translations = { en, de };

const BootstrapCheckbox = (props) => (
  <Checkbox
    {...props}
    icon={
      <span
        style={{
          width: 17,
          height: 17,
          border: "2px solid #D0D5DD",   // gray border
          borderRadius: "0.5rem",       // Bootstrap style rounded
          backgroundColor: "#F9FAFB",    // light gray background
          display: "inline-block",
        }}
      />
    }
    checkedIcon={
      <span
        style={{
          width: 17,
          height: 17,
          border: "2px solid #0d6efd",   // blue border when checked
          borderRadius: "0.5rem",
          backgroundColor: "#0d6efd",    // Bootstrap blue fill
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="white"
        >
          <path d="M13.485 1.929a.75.75 0 0 1 .032 1.06l-7.25 8a.75.75 0 0 1-1.093.032l-3.25-3.25a.75.75 0 0 1 1.06-1.06l2.678 2.678 6.72-7.418a.75.75 0 0 1 1.103-.042z" />
        </svg>
      </span>
    }
  />
);

export default function FeedbackForm() {
  const { guestId } = useParams();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [formData, setFormData] = useState({
    name: "",
    language: (navigator.language || navigator.userLanguage).slice(0, 2) || "en",
    feedbackComments: "",
    quotation: "",
    recommendationRating: 0,
    recommendationComments: "",
    doNotUseCommentsForMarketing: false,
    subscribeToNewsletter: false,
    roomType: "Resort",
    roomNumber: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [feedbackAlreadySubmitted, setFeedbackAlreadySubmitted] = useState(false);
  const [feedbackId, setFeedbackId] = useState(null);
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false); // submission success
  const [feedbackCommentsError, setFeedbackCommentsError] = useState(false);
  const [exampleIndex, setExampleIndex] = useState(0);

 const exampleComments = [
  {
    en:
      "What a week on Pelagian! This is muck heaven, a window to a new world. But don’t let that fool you, the reefs and corals are amazing! Your enthusiasm for the big and tiny have brought us to a new level of appreciation for diving.",
    de:
      "Was für eine Woche auf der Pelagian! Das ist ein Paradies für Muck Diving, ein Fenster in eine neue Welt. Aber lassen Sie sich davon nicht täuschen, die Riffe und Korallen sind ebenso atemberaubend! Ihre Begeisterung für das Große wie auch für das Kleine hat uns zu einem ganz neuen Niveau der Wertschätzung für das Tauchen gebracht.",
    author: "Brian and Deena Jones – Guests",
  },
  {
    en:
      "The direct flight certainly influenced our decision to go to Wakatobi. The ease and speed of getting to the resort is superb, as is the reassurance that all your luggage will arrive at the same time as you do!",
    de:
      "Der Direktflug hat unsere Entscheidung, nach Wakatobi zu reisen, definitiv beeinflusst. Die Einfachheit und Schnelligkeit, mit der man das Resort erreicht, ist hervorragend, ebenso wie die Gewissheit, dass das gesamte Gepäck gleichzeitig mit einem selbst ankommt!",
    author: "Kathy Beech – Guest",
  },
  {
    en:
      "Wakatobi’s attention to detail and the staff attitude in doing everything they can to make things perfect for us really shows. We appreciated the combination of world-class diving and unpretentious luxury.",
    de:
      "Wakatobis Liebe zum Detail und die Einstellung der Mitarbeiter, alles zu tun, um es für uns perfekt zu machen, sind wirklich spürbar. Wir haben die Kombination aus Tauchen auf Weltklasseniveau und unaufdringlichem Luxus sehr geschätzt.",
    author: "David & Alison Karlin – Guests",
  },
];

  useEffect(() => {
    const id = setInterval(() => {
      setExampleIndex((prev) => (prev + 1) % exampleComments.length);
    }, 5000);
    return () => clearInterval(id);
  }, [exampleComments.length]);

  const getRecaptchaToken = async () => {
    try {
      if (!executeRecaptcha) {
        return "";
      }
      return await executeRecaptcha("submit");
    } catch (error) {
      console.error("Error getting reCAPTCHA token", error);
      return "";
    }
  };

  useEffect(() => {
    const fetchGuestFeedback = async () => {
      try {
        const response = await createFetch({
          path: `feedback/guest/${guestId}`,
          method: "GET",
        });
  
        const userLang = (navigator.language || navigator.userLanguage).slice(0, 2) || "en";
        const data = response.guestRecord;
  
        setFormData((prev) => ({
          ...prev,
          name: data.GuestFullName || "",
          email: data.Email__c || "",
          language: userLang,
        }));
      } catch (error) {
        const lang = formData.language || "en";
        if (error.message === "Feedback already submitted") {
          setFeedbackAlreadySubmitted(true);
          setFeedbackId(error.feedbackId || null);
          alert(`${translations[lang]?.alerts.feedbackSubmitted || translations.en.alerts.feedbackSubmitted}${error.feedbackId ? ` (ID: ${error.feedbackId})` : ""}`);
        } else if (error.name === "TypeError") {
          // alert(translations[lang]?.alerts.networkError || translations.en.alerts.networkError);
        } else {
          console.error("Unexpected error fetching guest feedback:", error);
          alert(`${translations[lang]?.alerts.unexpectedError || translations.en.alerts.unexpectedError} ${guestId && `Invalid ID`}`);
        }
      }
       finally {
        setIsLoading(false);
      }
    };
  
    if (guestId) fetchGuestFeedback();
  }, [guestId, formData.language]);
  

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (name === "feedbackComments") {
      setFeedbackCommentsError(!value.trim());
    }
  };

  const handleRatingChange = (_, newValue) => {
    setFormData((prev) => ({
      ...prev,
      recommendationRating: newValue || 0,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate required feedback comments
    if (!formData.feedbackComments || !formData.feedbackComments.trim()) {
      setFeedbackCommentsError(true);
      setIsSubmitting(false);
      const lang = formData.language || "en";
      alert(translations[lang]?.feedbackForm.feedbackRequired || translations.en.feedbackForm.feedbackRequired);
      const el = document.getElementById("feedbackComments");
      if (el) el.focus();
      return;
    }

    let recaptchaToken = await getRecaptchaToken();
    if (!recaptchaToken) {
      setIsSubmitting(false);
      alert("reCAPTCHA verification failed. Please retry.");
      return;
    }

    try {
      const langCode = (formData.language || "").slice(0, 2);

      const response = await createFetch({
        path: `feedback`,
        method: "POST",
        body: {
          guestId: guestId || "",
          name: formData.name,
          email: guestId ? "" : formData.email,
          language: langCode === "de" ? "Deutsch" : langCode === "en" ? "English" : "",
          feedbackComments: formData.feedbackComments,
          wakatobiQuotationEn: langCode === "en" ? formData.quotation : "",
          wakatobiQuotationDe: langCode === "de" ? formData.quotation : "",
          recommendationComments: formData.recommendationComments,
          doNotUseCommentsForMarketing: formData.doNotUseCommentsForMarketing,
          subscribeToNewsletter: formData.subscribeToNewsletter,
          relatedTo: formData.roomType,
          roomNumber: formData.roomNumber || "",
          recaptchaToken,
        },
      });

      setIsSubmitSuccess(true);
    } catch (error) {
      const lang = formData.language || "en";
      alert(`${translations[lang]?.alerts.submitError || translations.en.alerts.submitError}` + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // If submission was successful, show only the thank you card
  if (isSubmitSuccess) {
    return <ThankYouCard shareText={formData.feedbackComments} lang={formData.language}/>;
  }

  // Reusable inline style
  const inputStyle = { fontSize: "16px", marginTop: '0 !important' };
  const labelStyle = { fontSize: "14px" };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fff", display: "flex", flexDirection: "column", alignItems: "center", pb: 10 }}>
      {/* Banner */}
      <Box
        sx={{
          width: "100%",
          height: { xs: 350, sm: 350, md: 550 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundImage: `url(${require("../../static/img/bg-feedback.png")})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          textAlign: "center",
          px: 2,
          position: "relative", // important for overlay
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: "rgba(0,0,0,0.1)", // 10% opacity
            zIndex: 0,
          }}
        />

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            maxWidth: 800,
            px: { xs: 1, sm: 2 },
            mt: { xs: -5, md: -10 }
          }}
        >
          <Box
            component="img"
            src={require("../../static/img/wakatobi-white.png")}
            alt="logo-wakatobi"
            sx={{
              width: { xs: 200, md: 250 },
              mb: { xs: 1, md: 2 },
            }}
          />
          <Box
            component="h2"
            sx={{
              color: "white",
              fontWeight: 600,
              fontSize: { xs: "2.5rem", md: "3rem", lg: "4rem" },
              lineHeight: 1.3,
            }}
          >
            {translations[formData.language]?.feedbackForm.bannerTitle || translations.en.feedbackForm.bannerTitle}
          </Box>
        </Box>
      </Box>

      <Card sx={{ position: "relative", maxWidth: 900, width: "100%", mt: { xs: -5, md: -10 }, mx: 2 }}>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <h3 style={{fontSize: "18px", margin: 0}}>{translations[formData.language]?.feedbackForm.subtitle || translations.en.feedbackForm.subtitle}</h3>
              <Box sx={{ display: 'flex', borderRadius: '4px' }}>
                <Button 
                  onClick={() => handleChange({ target: { name: 'language', value: 'en' } })}
                  sx={{
                    backgroundColor: formData.language === 'en' ? '#1e2eb8' : 'transparent',
                    color: formData.language === 'en' ? 'white' : 'black',
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                  }}
                >
                  <span style={{fontSize: "14px", fontWeight: 600}}>
                    EN
                  </span>
                </Button>
                <Button 
                  onClick={() => handleChange({ target: { name: 'language', value: 'de' } })}
                  sx={{
                    backgroundColor: formData.language === 'de' ? '#1e2eb8' : 'transparent',
                    color: formData.language === 'de' ? 'white' : 'black',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  }}
                >
                  <span style={{fontSize: "14px", fontWeight: 600}}>
                    DE
                  </span>
                </Button>
              </Box>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <span style={labelStyle}>
                  {translations[formData.language]?.feedbackForm.nameLabel || translations.en.feedbackForm.nameLabel}
                  <span style={{ color: '#d92d20' }}> *</span>
                </span>
                <TextField
                  fullWidth
                  placeholder={translations[formData.language]?.feedbackForm.namePlaceholder || translations.en.feedbackForm.namePlaceholder}
                  variant="outlined"
                  size="small"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  InputProps={{ style: inputStyle }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <span style={labelStyle}>
                  {translations[formData.language]?.feedbackForm.emailLabel || translations.en.feedbackForm.emailLabel}
                  <span style={{ color: '#d92d20' }}> *</span>
                </span>
                <TextField
                  fullWidth
                  placeholder={translations[formData.language]?.feedbackForm.emailPlaceholder || translations.en.feedbackForm.emailPlaceholder}
                  variant="outlined"
                  size="small"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  InputProps={{style: inputStyle }}
                />
              </Grid>
            </Grid>

            <Box mt={2}>
              <span style={labelStyle}>
                {translations[formData.language]?.feedbackForm.feedbackLabel || translations.en.feedbackForm.feedbackLabel}
                <span style={{ color: '#d92d20' }}> *</span>
              </span>
              <TextField
                fullWidth
                placeholder={translations[formData.language]?.feedbackForm.feedbackPlaceholder || translations.en.feedbackForm.feedbackPlaceholder}
                margin="normal"
                variant="outlined"
                multiline
                rows={5}
                name="feedbackComments"
                value={formData.feedbackComments}
                onChange={handleChange}
                id="feedbackComments"
                required
                error={feedbackCommentsError}
                helperText={feedbackCommentsError ? (translations[formData.language]?.feedbackForm.feedbackRequired || translations.en.feedbackForm.feedbackRequired) : ""}
                InputProps={{ style: inputStyle }}
              />
            </Box>


            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <span style={labelStyle}>
                  {translations[formData.language]?.feedbackForm.propertyToggleLabel || translations.en.feedbackForm.propertyToggleLabel}
                </span>
                <Select
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={formData.roomType || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      roomType: e.target.value,
                    }))
                  }
                  displayEmpty
                  sx={{ mt: 2, "& .MuiSelect-select": { display: "flex", alignItems: "center", fontSize: "1.6rem" } }}
                >
                  <MenuItem value="Resort">
                    {translations[formData.language]?.feedbackForm.propertyResort || translations.en.feedbackForm.propertyResort}
                  </MenuItem>
                  <MenuItem value="Pelagian">
                    {translations[formData.language]?.feedbackForm.propertyPelagian || translations.en.feedbackForm.propertyPelagian}
                  </MenuItem>
                </Select>
              </Grid>
              <Grid item xs={12} sm={6}>
                <span style={labelStyle}>
                  {translations[formData.language]?.feedbackForm.roomNumberLabel || translations.en.feedbackForm.roomNumberLabel}
                </span>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  name="roomNumber"
                  value={formData.roomNumber || ""}
                  onChange={handleChange}
                  InputProps={{ style: {...inputStyle} }}
                />
              </Grid>
            </Grid>
            <Box mt={2}>
              <span style={labelStyle}>{translations[formData.language]?.feedbackForm.quotationLabel || translations.en.feedbackForm.quotationLabel}</span>
              <TextField
                fullWidth
                margin="normal"
                variant="outlined"
                multiline
                rows={4}
                inputProps={{ maxLength: 500, style: inputStyle }}
                name="quotation"
                value={formData.quotation}
                onChange={handleChange}
              />
              <small style={{...labelStyle, color: '#667085', fontWeight: 300}}>{translations[formData.language]?.feedbackForm.quotationHelpText || translations.en.feedbackForm.quotationHelpText}</small>
            </Box>

            <Box mt={3} sx={{ display: "flex", flexDirection: "column" }}>
              <h3 style={{fontSize: "18px"}}>{translations[formData.language]?.feedbackForm.preferencesTitle || translations.en.feedbackForm.preferencesTitle}</h3>
              <FormControlLabel
                control={
                  <BootstrapCheckbox
                    name="doNotUseCommentsForMarketing"
                    checked={formData.doNotUseCommentsForMarketing}
                    onChange={handleChange}
                  />
                }
                label={
                  <span style={labelStyle}>
                    {translations[formData.language]?.feedbackForm.marketingOptOut || translations.en.feedbackForm.marketingOptOut}
                  </span>
                }
              />
              <FormControlLabel
                control={
                  <BootstrapCheckbox
                    name="subscribeToNewsletter"
                    checked={formData.subscribeToNewsletter}
                    onChange={handleChange}
                  />
                }
                label={
                  <span style={labelStyle}>
                    {translations[formData.language]?.feedbackForm.newsletterOptIn || translations.en.feedbackForm.newsletterOptIn}
                  </span>
                }
              />
            </Box>

            <Button
              variant="contained"
              type="submit"
              fullWidth
              disabled={isSubmitting || feedbackAlreadySubmitted}
              sx={{ mt: 3, bgcolor: "#1e2eb8", color: "white", fontSize: '18px', textTransform: "none", }}
            >
              {isSubmitting ? (translations[formData.language]?.feedbackForm.submittingButton || translations.en.feedbackForm.submittingButton) : (translations[formData.language]?.feedbackForm.submitButton || translations.en.feedbackForm.submitButton)}
            </Button>

            <Box mt={2} sx={{ bgcolor: "#f9fafa", borderRadius: 2, p: 2 }}>
              <Box sx={{ display: "block", textAlign: "center" }}>
                <Box sx={{ width: "100%", px: 2, fontStyle: "italic", fontSize: "14px" }}>
                  “{exampleComments[exampleIndex][formData.language]}”
                </Box>
                <Box sx={{ mt: 1, fontStyle: "bold", fontSize: "14px" }}>
                  {exampleComments[exampleIndex].author}
                </Box>
              </Box>
              <Box sx={{ mt: 1, display: "flex", justifyContent: "center", gap: 0.5 }}>
                {exampleComments.map((_, i) => (
                  <Box
                    key={i}
                    onClick={() => setExampleIndex(i)}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: i === exampleIndex ? "#1e2eb8" : "#D0D5DD",
                      cursor: "pointer",
                    }}
                  />
                ))}
              </Box>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
